import React, { useState, useEffect, useRef } from "react";
import { 
  Play, 
  Save, 
  RotateCcw, 
  Settings, 
  Terminal, 
  Check, 
  AlertCircle, 
  ChevronDown, 
  Sliders, 
  Maximize2, 
  Minimize2, 
  X, 
  Copy, 
  BookOpen, 
  Code2,
  FileCode
} from "lucide-react";
import { PINE_TEMPLATES } from "../services/pineTemplates";
import { extractPineInputs } from "../services/pineScriptEngine";

export default function PineEditor({
  isOpen,
  onClose,
  currentCode,
  onChangeCode,
  onRunScript,
  onRemoveFromChart,
  isPineApplied,
  executionResult,
  customInputs,
  onChangeInputs,
  currentSymbol,
  currentTimeframe
}) {
  const [selectedTemplateId, setSelectedTemplateId] = useState("msb_dynamic_indicator");
  const [isConsoleOpen, setIsConsoleOpen] = useState(true);
  const [isInputsModalOpen, setIsInputsModalOpen] = useState(false);
  const [isMaximized, setIsMaximized] = useState(false);
  const [savedScripts, setSavedScripts] = useState([]);
  const [scriptName, setScriptName] = useState("MSB Dynamic Structure");
  const [copySuccess, setCopySuccess] = useState(false);

  const textareaRef = useRef(null);
  const lineNumbersRef = useRef(null);

  // Load saved scripts from localStorage
  useEffect(() => {
    try {
      const stored = localStorage.getItem("pine_saved_scripts");
      if (stored) {
        setSavedScripts(JSON.parse(stored));
      }
    } catch (e) {}
  }, []);

  // Sync line numbers on scroll
  const handleScroll = () => {
    if (textareaRef.current && lineNumbersRef.current) {
      lineNumbersRef.current.scrollTop = textareaRef.current.scrollTop;
    }
  };

  // Handle Tab key in textarea
  const handleKeyDown = (e) => {
    if (e.key === "Tab") {
      e.preventDefault();
      const textarea = textareaRef.current;
      if (!textarea) return;
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const val = textarea.value;
      const newVal = val.substring(0, start) + "    " + val.substring(end);
      onChangeCode(newVal);
      setTimeout(() => {
        textarea.selectionStart = textarea.selectionEnd = start + 4;
      }, 0);
    } else if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      onRunScript();
    }
  };

  const handleSelectTemplate = (templateId) => {
    const tpl = PINE_TEMPLATES.find((t) => t.id === templateId);
    if (tpl) {
      setSelectedTemplateId(templateId);
      setScriptName(tpl.name);
      onChangeCode(tpl.code);
    }
  };

  const handleClearCode = () => {
    if (window.confirm("Bạn có chắc muốn xóa sạch mã trong Pine Editor và gỡ khỏi biểu đồ?")) {
      onChangeCode("");
      if (onRemoveFromChart) onRemoveFromChart();
    }
  };

  const handleSaveScript = () => {
    const name = prompt("Nhập tên cho kịch bản Pine Script của bạn:", scriptName || "My Pine Strategy");
    if (!name) return;
    const newScript = {
      id: `script_${Date.now()}`,
      name,
      code: currentCode,
      updatedAt: new Date().toLocaleDateString("vi-VN")
    };
    const updated = [...savedScripts.filter((s) => s.name !== name), newScript];
    setSavedScripts(updated);
    setScriptName(name);
    try {
      localStorage.setItem("pine_saved_scripts", JSON.stringify(updated));
    } catch (e) {}
    alert(`Đã lưu kịch bản "${name}" vào bộ nhớ trình duyệt thành công!`);
  };

  const handleCopyCode = () => {
    navigator.clipboard.writeText(currentCode);
    setCopySuccess(true);
    setTimeout(() => setCopySuccess(false), 2000);
  };

  if (!isOpen) return null;

  const parsedInputs = extractPineInputs(currentCode);
  const linesCount = (currentCode || "").split("\n").length;
  const lineNumbers = Array.from({ length: Math.max(linesCount, 1) }, (_, i) => i + 1);

  return (
    <div
      className={`fixed bottom-0 left-0 right-0 z-40 bg-slate-900 border-t border-slate-700 shadow-2xl flex flex-col font-sans transition-all duration-200 ${
        isMaximized ? "top-14 h-[calc(100vh-56px)]" : "h-96"
      }`}
    >
      {/* Top Action Toolbar */}
      <div className="flex items-center justify-between px-4 py-2 border-b border-slate-800 bg-slate-950/80 backdrop-blur select-none">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 text-cyan-400 font-bold text-xs bg-cyan-950/40 border border-cyan-500/30 px-2.5 py-1 rounded-lg">
            <Code2 className="w-4 h-4 text-cyan-400" />
            <span>Pine Script™ v5</span>
          </div>

          {/* Status Badge */}
          <div className="flex items-center gap-1.5 text-[11px] px-2 py-0.5 rounded-full border">
            {isPineApplied ? (
              <span className="flex items-center gap-1 text-emerald-400 border-emerald-500/30 bg-emerald-950/40 px-2 py-0.5 rounded-full font-semibold">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                Đang hiển thị trên biểu đồ
              </span>
            ) : (
              <span className="flex items-center gap-1 text-slate-400 border-slate-700 bg-slate-900/60 px-2 py-0.5 rounded-full">
                <span className="w-1.5 h-1.5 rounded-full bg-slate-500" />
                Chưa nạp lên biểu đồ
              </span>
            )}
          </div>

          {/* Template Dropdown */}
          <div className="relative flex items-center">
            <select
              value={selectedTemplateId}
              onChange={(e) => handleSelectTemplate(e.target.value)}
              className="bg-slate-900 border border-slate-700 hover:border-slate-600 rounded-lg px-3 py-1 text-slate-200 text-xs focus:outline-none focus:border-cyan-500 cursor-pointer max-w-xs truncate"
            >
              <optgroup label="📋 Kịch bản mẫu có sẵn">
                {PINE_TEMPLATES.map((tpl) => (
                  <option key={tpl.id} value={tpl.id}>
                    {tpl.name}
                  </option>
                ))}
              </optgroup>
              {savedScripts.length > 0 && (
                <optgroup label="💾 Kịch bản của bạn">
                  {savedScripts.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name} ({s.updatedAt})
                    </option>
                  ))}
                </optgroup>
              )}
            </select>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center gap-2">
          {/* Inputs Config Button */}
          {parsedInputs.length > 0 && (
            <button
              onClick={() => setIsInputsModalOpen(true)}
              className="flex items-center gap-1.5 px-3 py-1 bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-semibold rounded-lg border border-slate-700 transition cursor-pointer"
              title="Tùy chỉnh thông số đầu vào"
            >
              <Sliders className="w-3.5 h-3.5 text-amber-400" />
              <span>Inputs</span>
              <span className="px-1.5 py-0.2 bg-amber-500/20 text-amber-300 rounded text-[10px] font-mono">
                {parsedInputs.length}
              </span>
            </button>
          )}

          {/* Clear Button */}
          <button
            onClick={handleClearCode}
            className="p-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-rose-400 transition cursor-pointer"
            title="Xóa sạch mã & Gỡ khỏi biểu đồ"
          >
            <RotateCcw className="w-4 h-4" />
          </button>

          {/* Copy Button */}
          <button
            onClick={handleCopyCode}
            className="p-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 transition cursor-pointer"
            title="Sao chép toàn bộ mã"
          >
            {copySuccess ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
          </button>

          {/* Save Script Button */}
          <button
            onClick={handleSaveScript}
            className="flex items-center gap-1 px-2.5 py-1 bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-semibold rounded-lg border border-slate-700 transition cursor-pointer"
            title="Lưu kịch bản"
          >
            <Save className="w-3.5 h-3.5 text-purple-400" />
            <span className="hidden sm:inline">Lưu</span>
          </button>

          {/* Remove from Chart Button if applied */}
          {isPineApplied && (
            <button
              onClick={onRemoveFromChart}
              className="flex items-center gap-1 px-3 py-1.5 bg-rose-950/60 hover:bg-rose-900 text-rose-300 text-xs font-bold rounded-lg border border-rose-600/40 transition cursor-pointer"
              title="Gỡ kịch bản ra khỏi biểu đồ"
            >
              <X className="w-3.5 h-3.5" />
              <span>Gỡ khỏi Biểu đồ</span>
            </button>
          )}

          {/* Main "Add to Chart" / Run Button */}
          <button
            onClick={onRunScript}
            className="flex items-center gap-1.5 px-4 py-1.5 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white text-xs font-bold rounded-lg shadow-lg shadow-emerald-950/40 transition active:scale-95 cursor-pointer"
            title="Nạp và chạy kịch bản lên biểu đồ (Ctrl + Enter)"
          >
            <Play className="w-3.5 h-3.5 fill-current" />
            <span>{isPineApplied ? "Cập nhật Biểu đồ" : "Nạp vào Biểu đồ"}</span>
          </button>

          {/* Console Output Toggle */}
          <button
            onClick={() => setIsConsoleOpen(!isConsoleOpen)}
            className={`p-1.5 rounded-lg border transition cursor-pointer ${
              isConsoleOpen
                ? "bg-cyan-500/20 border-cyan-500/40 text-cyan-300"
                : "bg-slate-800 border-slate-700 text-slate-400 hover:text-slate-200"
            }`}
            title="Bật/tắt khung Console Log"
          >
            <Terminal className="w-4 h-4" />
          </button>

          {/* Maximize / Restore */}
          <button
            onClick={() => setIsMaximized(!isMaximized)}
            className="p-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-slate-200 transition cursor-pointer"
            title={isMaximized ? "Thu nhỏ" : "Toàn màn hình"}
          >
            {isMaximized ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
          </button>

          {/* Close Editor Panel */}
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-slate-800 text-slate-400 hover:text-slate-200 transition cursor-pointer"
            title="Đóng Pine Editor"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Main Code Editing Workspace */}
      <div className="flex-1 flex overflow-hidden relative">
        {/* Line Numbers Column */}
        <div
          ref={lineNumbersRef}
          className="w-12 py-3 bg-slate-950/90 border-r border-slate-800 text-right pr-3 select-none text-slate-600 font-mono text-xs overflow-hidden leading-5"
        >
          {lineNumbers.map((n) => (
            <div key={n}>{n}</div>
          ))}
        </div>

        {/* Code Textarea Area */}
        <textarea
          ref={textareaRef}
          value={currentCode}
          onChange={(e) => onChangeCode(e.target.value)}
          onScroll={handleScroll}
          onKeyDown={handleKeyDown}
          placeholder="// Nhập hoặc dán mã nguồn Pine Script v5 tại đây..."
          spellCheck="false"
          className="flex-1 py-3 px-4 bg-slate-900/95 text-slate-100 font-mono text-xs leading-5 focus:outline-none resize-none overflow-auto selection:bg-cyan-500/30 whitespace-pre"
        />
      </div>

      {/* Bottom Console / Output Drawer */}
      {isConsoleOpen && (
        <div className="h-28 border-t border-slate-800 bg-slate-950 px-4 py-2 flex flex-col font-mono text-[11px] overflow-hidden">
          <div className="flex items-center justify-between pb-1 mb-1 border-b border-slate-900 text-slate-500">
            <div className="flex items-center gap-2">
              <Terminal className="w-3.5 h-3.5 text-cyan-400" />
              <span className="font-bold text-slate-400">PINE COMPILER LOGS & BACKTEST</span>
            </div>
            {executionResult && (
              <span className={executionResult.success ? "text-emerald-400 font-semibold" : "text-rose-400 font-semibold"}>
                {executionResult.success ? "● Sẵn sàng (Ready)" : "● Phát hiện lỗi"}
              </span>
            )}
          </div>

          <div className="flex-1 overflow-y-auto space-y-0.5 text-slate-300">
            {executionResult?.logs && executionResult.logs.length > 0 ? (
              executionResult.logs.map((log, idx) => (
                <div key={idx} className="leading-tight">
                  <span className="text-slate-500 mr-2">&gt;</span>
                  <span className={log.includes("Lỗi") ? "text-rose-400 font-semibold" : "text-slate-300"}>
                    {log}
                  </span>
                </div>
              ))
            ) : (
              <div className="text-slate-500 italic">
                Chưa có dữ liệu biên dịch. Nhấn "Nạp vào Biểu đồ" (hoặc Ctrl+Enter) để thực thi kịch bản.
              </div>
            )}
            {executionResult?.error && (
              <div className="text-rose-400 font-semibold flex items-center gap-1.5 mt-1">
                <AlertCircle className="w-3.5 h-3.5 text-rose-500 shrink-0" />
                <span>{executionResult.error}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Dynamic Inputs Modal */}
      {isInputsModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 backdrop-blur-sm p-4 animate-fadeIn">
          <div className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-lg shadow-2xl overflow-hidden flex flex-col max-h-[80vh]">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-800 bg-slate-950/60">
              <div className="flex items-center gap-2">
                <Sliders className="w-5 h-5 text-amber-400" />
                <h3 className="font-bold text-sm text-slate-100">Cài Đặt Đầu Vào (Pine Script Inputs)</h3>
              </div>
              <button
                onClick={() => setIsInputsModalOpen(false)}
                className="p-1 rounded-lg text-slate-400 hover:text-slate-200 hover:bg-slate-800 transition cursor-pointer"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="p-6 overflow-y-auto space-y-4 text-xs">
              {parsedInputs.map((inp) => {
                const currentVal = customInputs[inp.varName] !== undefined ? customInputs[inp.varName] : inp.defaultValue;
                return (
                  <div key={inp.varName} className="flex items-center justify-between p-2.5 rounded-xl bg-slate-950/60 border border-slate-800">
                    <div>
                      <span className="text-slate-200 font-semibold block">{inp.title}</span>
                      <span className="text-[10px] text-slate-500 font-mono">{inp.varName} ({inp.type})</span>
                    </div>

                    <div>
                      {inp.type === "bool" ? (
                        <input
                          type="checkbox"
                          checked={Boolean(currentVal)}
                          onChange={(e) => onChangeInputs({ ...customInputs, [inp.varName]: e.target.checked })}
                          className="w-4 h-4 accent-cyan-400 rounded cursor-pointer"
                        />
                      ) : inp.options && inp.options.length > 0 ? (
                        <select
                          value={currentVal}
                          onChange={(e) => onChangeInputs({ ...customInputs, [inp.varName]: e.target.value })}
                          className="bg-slate-900 border border-slate-700 rounded-lg px-2.5 py-1 text-slate-200 text-xs focus:outline-none focus:border-cyan-500"
                        >
                          {inp.options.map((opt) => (
                            <option key={opt} value={opt}>
                              {opt}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <input
                          type="number"
                          step={inp.step || (inp.type === "float" ? 0.1 : 1)}
                          min={inp.min !== null ? inp.min : undefined}
                          max={inp.max !== null ? inp.max : undefined}
                          value={currentVal}
                          onChange={(e) =>
                            onChangeInputs({
                              ...customInputs,
                              [inp.varName]: inp.type === "int" ? parseInt(e.target.value) || 0 : parseFloat(e.target.value) || 0
                            })
                          }
                          className="w-24 bg-slate-900 border border-slate-700 rounded-lg px-2.5 py-1 text-right text-slate-100 font-mono text-xs focus:outline-none focus:border-cyan-500"
                        />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="flex items-center justify-between px-6 py-3 border-t border-slate-800 bg-slate-950/60">
              <button
                onClick={() => {
                  const defaultObj = {};
                  parsedInputs.forEach((i) => { defaultObj[i.varName] = i.defaultValue; });
                  onChangeInputs(defaultObj);
                }}
                className="text-xs text-slate-400 hover:text-slate-200 transition cursor-pointer"
              >
                Đặt lại mặc định
              </button>

              <button
                onClick={() => {
                  setIsInputsModalOpen(false);
                  onRunScript();
                }}
                className="px-4 py-1.5 bg-cyan-600 hover:bg-cyan-500 text-white font-bold text-xs rounded-lg transition cursor-pointer"
              >
                Áp dụng & Chạy lại
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
